/**
 * Le Gala Theme JavaScript
 * Liquid Glass Design System
 */

(function() {
  'use strict';

  // ============================================
  // Utility Functions
  // ============================================

  const debounce = (fn, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  };

  const formatMoney = (cents, format = '${{amount}}') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = (cents / 100).toFixed(2);
    return format.replace('{{amount}}', value);
  };

  // ============================================
  // Sticky Header with Scroll Effects
  // ============================================

  class StickyHeader {
    constructor() {
      this.header = document.querySelector('.liquid-glass-nav');
      if (!this.header) return;

      this.scrollThreshold = 50;
      this.lastScrollY = 0;
      this.init();
    }

    init() {
      window.addEventListener('scroll', debounce(() => this.onScroll(), 10));
      this.onScroll();
    }

    onScroll() {
      const currentScrollY = window.scrollY;

      if (currentScrollY > this.scrollThreshold) {
        this.header.classList.add('liquid-glass-nav--scrolled');
      } else {
        this.header.classList.remove('liquid-glass-nav--scrolled');
      }

      this.lastScrollY = currentScrollY;
    }
  }

  // ============================================
  // Quantity Selector
  // ============================================

  class QuantitySelector {
    constructor(element) {
      this.element = element;
      this.input = element.querySelector('.liquid-glass-quantity__input');
      this.decreaseBtn = element.querySelector('[data-action="decrease"]');
      this.increaseBtn = element.querySelector('[data-action="increase"]');
      
      if (!this.input) return;
      
      this.min = parseInt(this.input.getAttribute('min')) || 1;
      this.max = parseInt(this.input.getAttribute('max')) || 999;
      
      this.init();
    }

    init() {
      this.decreaseBtn?.addEventListener('click', () => this.decrease());
      this.increaseBtn?.addEventListener('click', () => this.increase());
      this.input.addEventListener('change', () => this.validate());
      this.input.addEventListener('blur', () => this.validate());
    }

    decrease() {
      const currentValue = parseInt(this.input.value);
      if (currentValue > this.min) {
        this.input.value = currentValue - 1;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    increase() {
      const currentValue = parseInt(this.input.value);
      if (currentValue < this.max) {
        this.input.value = currentValue + 1;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    validate() {
      let value = parseInt(this.input.value);
      if (isNaN(value) || value < this.min) {
        value = this.min;
      } else if (value > this.max) {
        value = this.max;
      }
      this.input.value = value;
    }
  }

  // ============================================
  // Variant Selector
  // ============================================

  class VariantSelector {
    constructor(container) {
      this.container = container;
      this.productForm = container.closest('form');
      this.radioInputs = container.querySelectorAll('.liquid-glass-option input');
      this.selectInputs = container.querySelectorAll('select[name^="option"]');
      this.productData = null;
      
      this.init();
    }

    init() {
      // Look for product JSON in a script tag (more robust for special characters)
      const productContainer = this.container.closest('[data-product-id]');
      const productJsonScript = productContainer?.querySelector('script[data-product-json]');
      if (productJsonScript) {
        try {
          this.productData = JSON.parse(productJsonScript.textContent);
        } catch (e) {
          console.error('Failed to parse product data:', e);
        }
      }

      // Listen to radio button changes
      this.radioInputs.forEach(option => {
        option.addEventListener('change', (e) => {
          // Update visual selected state
          this.updateSelectedState(e.target);
          this.onOptionChange();
        });
      });

      // Listen to select dropdown changes
      this.selectInputs.forEach(select => {
        select.addEventListener('change', () => this.onOptionChange());
      });

      // Initialize selected states on load
      this.initSelectedStates();
    }

    initSelectedStates() {
      // For each option group, ensure only checked items have selected class
      this.container.querySelectorAll('.liquid-glass-options').forEach(group => {
        group.querySelectorAll('.liquid-glass-option').forEach(option => {
          const input = option.querySelector('input');
          if (input && input.checked) {
            option.classList.add('liquid-glass-option--selected');
          } else {
            option.classList.remove('liquid-glass-option--selected');
          }
        });
      });
    }

    updateSelectedState(changedInput) {
      // Find the parent options group
      const optionLabel = changedInput.closest('.liquid-glass-option');
      const optionsGroup = changedInput.closest('.liquid-glass-options');
      
      if (optionsGroup) {
        // Remove selected class from all options in this group
        optionsGroup.querySelectorAll('.liquid-glass-option').forEach(opt => {
          opt.classList.remove('liquid-glass-option--selected');
        });
        
        // Add selected class to the clicked option
        if (optionLabel) {
          optionLabel.classList.add('liquid-glass-option--selected');
        }
      }
    }

    onOptionChange() {
      // Build object using actual option names from inputs (option1, option2, etc.)
      const optionValues = {};
      
      // Get values from radio buttons
      this.container.querySelectorAll('.liquid-glass-option input:checked').forEach(input => {
        const optionName = input.name; // e.g., "option1", "option3"
        optionValues[optionName] = input.value;
      });
      
      // Get values from select dropdowns
      this.container.querySelectorAll('select[name^="option"]').forEach(select => {
        optionValues[select.name] = select.value;
      });

      // Get values from hidden inputs (for options like Location that are hidden from UI)
      this.container.querySelectorAll('input[type="hidden"][name^="option"]').forEach(input => {
        optionValues[input.name] = input.value;
      });

      if (this.productData) {
        const variant = this.findVariant(optionValues);
        if (variant) {
          this.updateVariantId(variant.id);
          this.updatePrice(variant);
          this.updateAvailability(variant);
          this.updateImage(variant);
        }
      }

      // Trigger custom event
      this.container.dispatchEvent(new CustomEvent('variant:changed', {
        bubbles: true,
        detail: { optionValues }
      }));
    }

    findVariant(optionValues) {
      return this.productData?.variants?.find(variant => {
        // Build comparison based on option positions
        // optionValues keys are like "option1", "option2", "option3"
        // variant.option1, variant.option2, variant.option3 are the actual values
        
        // Get all selected option values in order
        const selectedOption1 = optionValues['option1'];
        const selectedOption2 = optionValues['option2'];
        const selectedOption3 = optionValues['option3'];
        
        // Match all defined options
        const match1 = !selectedOption1 || variant.option1 === selectedOption1;
        const match2 = !selectedOption2 || variant.option2 === selectedOption2;
        const match3 = !selectedOption3 || variant.option3 === selectedOption3;
        
        return match1 && match2 && match3;
      });
    }

    updateVariantId(variantId) {
      const variantInput = this.productForm?.querySelector('[name="id"]');
      if (variantInput) {
        variantInput.value = variantId;
      }
    }

    updatePrice(variant) {
      const priceEl = document.querySelector('[data-product-price]');
      if (priceEl && variant.price) {
        priceEl.textContent = formatMoney(variant.price);
      }

      const comparePriceEl = document.querySelector('[data-product-compare-price]');
      if (comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePriceEl.textContent = formatMoney(variant.compare_at_price);
          comparePriceEl.style.display = '';
        } else {
          comparePriceEl.style.display = 'none';
        }
      }
    }

    updateAvailability(variant) {
      const addToCartBtn = this.productForm?.querySelector('[type="submit"]');
      if (addToCartBtn) {
        if (variant.available) {
          addToCartBtn.disabled = false;
          addToCartBtn.querySelector('.btn-text').textContent = 
            addToCartBtn.dataset.addToCart || 'Add to Cart';
        } else {
          addToCartBtn.disabled = true;
          addToCartBtn.querySelector('.btn-text').textContent = 
            addToCartBtn.dataset.soldOut || 'Sold Out';
        }
      }
    }

    updateImage(variant) {
      if (!variant.featured_image) return;

      const productContainer = this.container.closest('[data-product-id]');
      if (!productContainer) return;

      const mainImage = productContainer.querySelector('.product-gallery__main-image');
      if (!mainImage) return;

      const normalizeUrl = (url) => url.replace(/https?:/, '').split('?')[0];
      const variantImageBase = normalizeUrl(variant.featured_image.src);

      const thumbnails = productContainer.querySelectorAll('.product-gallery__thumbnail');
      let matchedThumb = null;

      thumbnails.forEach(thumb => {
        if (normalizeUrl(thumb.dataset.src || '') === variantImageBase) {
          matchedThumb = thumb;
        }
      });

      if (matchedThumb) {
        matchedThumb.click();
      } else {
        mainImage.src = variant.featured_image.src;
        mainImage.srcset = '';
        thumbnails.forEach(t => t.classList.remove('is-active'));
      }
    }
  }

  // ============================================
  // Add to Cart
  // ============================================

  class AddToCart {
    constructor(form) {
      this.form = form;
      this.submitBtn = form.querySelector('[type="submit"]');
      this.init();
    }

    init() {
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    async onSubmit(e) {
      e.preventDefault();

      if (this.submitBtn.disabled) return;

      const formData = new FormData(this.form);
      const submitText = this.submitBtn.querySelector('.btn-text');
      const originalText = submitText?.textContent;

      this.submitBtn.disabled = true;
      this.submitBtn.classList.add('loading');
      if (submitText) submitText.textContent = 'Adding...';

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error('Add to cart failed');
        }

        const item = await response.json();
        
        if (submitText) submitText.textContent = 'Added!';
        
        // Dispatch event for cart drawer
        document.dispatchEvent(new CustomEvent('cart:item-added', {
          detail: { item }
        }));

        // Update cart count
        this.updateCartCount();

        // Reset button after delay
        setTimeout(() => {
          if (submitText) submitText.textContent = originalText;
          this.submitBtn.disabled = false;
          this.submitBtn.classList.remove('loading');
        }, 2000);

      } catch (error) {
        console.error('Add to cart error:', error);
        if (submitText) submitText.textContent = 'Error';
        
        setTimeout(() => {
          if (submitText) submitText.textContent = originalText;
          this.submitBtn.disabled = false;
          this.submitBtn.classList.remove('loading');
        }, 2000);
      }
    }

    async updateCartCount() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        
        const countElements = document.querySelectorAll('[data-cart-count]');
        countElements.forEach(el => {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? '' : 'none';
        });
      } catch (error) {
        console.error('Failed to update cart count:', error);
      }
    }
  }

  // ============================================
  // Cart Drawer
  // ============================================

  class CartDrawer {
    constructor() {
      this.drawer = document.querySelector('.cart-drawer');
      this.overlay = document.querySelector('.cart-drawer-overlay');
      this.closeBtns = document.querySelectorAll('[data-cart-drawer-close]');
      this.openBtns = document.querySelectorAll('[data-cart-drawer-open]');
      
      if (!this.drawer) return;
      
      this.init();
    }

    init() {
      // Desktop breakpoint for cart drawer (768px+)
      this.desktopBreakpoint = 768;

      this.openBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          // Only open drawer on desktop, let mobile navigate to cart page
          if (window.innerWidth >= this.desktopBreakpoint) {
            e.preventDefault();
            this.open();
          }
          // On mobile, don't prevent default — link navigates to /cart
        });
      });

      // Use event delegation for close buttons (more reliable on mobile)
      document.addEventListener('click', (e) => {
        const closeBtn = e.target.closest('[data-cart-drawer-close]');
        if (closeBtn) {
          e.preventDefault();
          this.close();
        }
      });

      // Also handle touch events explicitly for mobile
      document.addEventListener('touchend', (e) => {
        const closeBtn = e.target.closest('[data-cart-drawer-close]');
        if (closeBtn) {
          e.preventDefault();
          this.close();
        }
      }, { passive: false });

      this.overlay?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('touchend', () => this.close());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });

      document.addEventListener('cart:item-added', () => {
        // On desktop, refresh drawer and open it
        // On mobile, redirect to cart page
        if (window.innerWidth >= this.desktopBreakpoint) {
          this.refresh();
          this.open();
        } else {
          window.location.href = '/cart';
        }
      });

      // Cart drawer quantity and remove buttons
      this.drawer.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-action]');
        if (!btn) return;

        e.preventDefault();
        e.stopPropagation();

        const action = btn.dataset.action;
        const line = parseInt(btn.dataset.line);

        // Disable button to prevent double clicks
        btn.disabled = true;
        btn.style.opacity = '0.5';

        if (action === 'increase') {
          this.updateQuantity(line, 1);
        } else if (action === 'decrease') {
          this.updateQuantity(line, -1);
        } else if (action === 'remove') {
          this.removeItem(line);
        }
      });
    }

    isOpen() {
      return this.drawer.classList.contains('is-open');
    }

    open() {
      this.drawer.classList.add('is-open');
      this.overlay?.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.drawer.classList.remove('is-open');
      this.overlay?.classList.remove('is-visible');
      document.body.style.overflow = '';
    }

    async updateQuantity(line, change) {
      try {
        // First get current cart to find current quantity
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        const item = cart.items[line - 1];
        
        if (!item) return;

        const newQuantity = Math.max(0, item.quantity + change);
        
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line, quantity: newQuantity })
        });

        this.refresh();
        this.updateCartCount();
      } catch (error) {
        console.error('Failed to update quantity:', error);
      }
    }

    async removeItem(line) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line, quantity: 0 })
        });

        if (!response.ok) {
          throw new Error('Failed to remove item');
        }

        await this.refresh();
        await this.updateCartCount();
      } catch (error) {
        console.error('Failed to remove item:', error);
        // Fallback: reload the page
        window.location.reload();
      }
    }

    async updateCartCount() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        
        const countElements = document.querySelectorAll('[data-cart-count]');
        countElements.forEach(el => {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? '' : 'none';
        });
      } catch (error) {
        console.error('Failed to update cart count:', error);
      }
    }

    async refresh() {
      try {
        // Fetch the cart drawer section using Shopify's section rendering API
        const response = await fetch(`${window.location.pathname}?sections=cart-drawer`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch cart');
        }
        
        const data = await response.json();
        
        if (data['cart-drawer']) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = data['cart-drawer'];
          
          const newDrawer = tempDiv.querySelector('.cart-drawer');
          if (newDrawer) {
            // Update content and footer
            const newContent = newDrawer.querySelector('.cart-drawer__content');
            const newFooter = newDrawer.querySelector('.cart-drawer__footer');
            const currentContent = this.drawer.querySelector('.cart-drawer__content');
            const currentFooter = this.drawer.querySelector('.cart-drawer__footer');
            
            if (newContent && currentContent) {
              currentContent.innerHTML = newContent.innerHTML;
            }
            
            // Handle footer updates (appears/disappears based on cart contents)
            if (currentFooter && newFooter) {
              currentFooter.outerHTML = newFooter.outerHTML;
            } else if (!currentFooter && newFooter) {
              this.drawer.insertAdjacentHTML('beforeend', newFooter.outerHTML);
            } else if (currentFooter && !newFooter) {
              currentFooter.remove();
            }
          }
        } else {
          // If sections API didn't work, reload the page
          window.location.reload();
        }
      } catch (error) {
        console.error('Failed to refresh cart drawer:', error);
        // Fallback: reload page
        window.location.reload();
      }
    }
  }

  // ============================================
  // Mobile Menu
  // ============================================

  class MobileMenu {
    constructor() {
      this.menu = document.querySelector('.mobile-menu');
      this.openBtn = document.querySelector('[data-mobile-menu-open]');
      this.closeBtn = document.querySelector('[data-mobile-menu-close]');
      this.overlay = document.querySelector('.mobile-menu-overlay');
      
      if (!this.menu) return;
      
      this.init();
    }

    init() {
      this.openBtn?.addEventListener('click', () => this.open());
      this.closeBtn?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click', () => this.close());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });
    }

    isOpen() {
      return this.menu.classList.contains('is-open');
    }

    open() {
      this.menu.classList.add('is-open');
      this.overlay?.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.menu.classList.remove('is-open');
      this.overlay?.classList.remove('is-visible');
      document.body.style.overflow = '';
    }
  }

  // ============================================
  // Product Image Gallery
  // ============================================

  class ProductGallery {
    constructor(container) {
      this.container = container;
      this.mainImage = container.querySelector('.product-gallery__main img');
      this.thumbnails = container.querySelectorAll('.product-gallery__thumbnail');
      
      if (!this.mainImage || !this.thumbnails.length) return;
      
      this.init();
    }

    init() {
      this.thumbnails.forEach(thumb => {
        thumb.addEventListener('click', () => {
          const newSrc = thumb.dataset.src;
          const newSrcset = thumb.dataset.srcset;
          
          if (newSrc) {
            this.mainImage.src = newSrc;
            if (newSrcset) {
              this.mainImage.srcset = newSrcset;
            }
          }

          this.thumbnails.forEach(t => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
        });
      });
    }
  }

  // ============================================
  // Scroll Animations (Intersection Observer)
  // ============================================

  class ScrollAnimator {
    constructor() {
      this.animatedElements = document.querySelectorAll('[data-animate]');
      if (!this.animatedElements.length) return;
      
      // Check for reduced motion preference
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.animatedElements.forEach(el => el.classList.add('is-visible'));
        return;
      }

      this.init();
    }

    init() {
      const options = {
        root: null,
        rootMargin: '0px 0px -80px 0px',
        threshold: 0.1
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            // Optionally unobserve after animation
            // this.observer.unobserve(entry.target);
          }
        });
      }, options);

      this.animatedElements.forEach(el => {
        this.observer.observe(el);
      });
    }
  }

  // Auto-apply animations to common elements
  function applyScrollAnimations() {
    // Product cards in grids
    document.querySelectorAll('.collection__item, .featured-collection__item').forEach((el, index) => {
      if (!el.hasAttribute('data-animate')) {
        el.setAttribute('data-animate', 'fade-up');
        el.setAttribute('data-animate-delay', (index % 4) + 1);
      }
    });

    // Section headers
    document.querySelectorAll('.collection__header, .featured-collection__header, .page-content__header').forEach(el => {
      if (!el.hasAttribute('data-animate')) {
        el.setAttribute('data-animate', 'fade-up');
      }
    });

    // Hero content
    document.querySelectorAll('.hero__content').forEach(el => {
      if (!el.hasAttribute('data-animate')) {
        el.setAttribute('data-animate', 'fade-up');
      }
    });

    // Product page elements
    document.querySelectorAll('.product__gallery').forEach(el => {
      if (!el.hasAttribute('data-animate')) {
        el.setAttribute('data-animate', 'fade-right');
      }
    });

    document.querySelectorAll('.product__info').forEach(el => {
      if (!el.hasAttribute('data-animate')) {
        el.setAttribute('data-animate', 'fade-left');
      }
    });

    // Footer
    document.querySelectorAll('.footer__brand, .footer__menu, .footer__newsletter').forEach((el, index) => {
      if (!el.hasAttribute('data-animate')) {
        el.setAttribute('data-animate', 'fade-up');
        el.setAttribute('data-animate-delay', index + 1);
      }
    });

    // Liquid glass cards
    document.querySelectorAll('.liquid-glass-card:not([data-animate])').forEach((el, index) => {
      el.setAttribute('data-animate', 'fade-up');
    });
  }

  // ============================================
  // Initialize
  // ============================================

  document.addEventListener('DOMContentLoaded', () => {
    // Apply scroll animations to elements
    applyScrollAnimations();
    
    // Initialize scroll animator
    new ScrollAnimator();
    // Sticky header
    new StickyHeader();

    // Quantity selectors
    document.querySelectorAll('.liquid-glass-quantity').forEach(el => {
      new QuantitySelector(el);
    });

    // Variant selectors
    document.querySelectorAll('[data-variant-selector]').forEach(el => {
      new VariantSelector(el);
    });

    // Add to cart forms
    document.querySelectorAll('form[action="/cart/add"]').forEach(form => {
      new AddToCart(form);
    });

    // Cart drawer
    new CartDrawer();

    // Mobile menu
    new MobileMenu();

    // Product galleries
    document.querySelectorAll('.product-gallery').forEach(el => {
      new ProductGallery(el);
    });

    // Blur search input when clicking product cards (prevents zoom persistence on mobile)
    if (document.querySelector('.search-page')) {
      const searchInput = document.querySelector('.search-page__input');
      const productCards = document.querySelectorAll('.search-page__item a');
      
      if (searchInput && productCards.length) {
        productCards.forEach(card => {
          card.addEventListener('click', () => {
            searchInput.blur();
          });
        });
      }
    }
  });

})();

